import { useState, type ReactNode } from 'react';
import { observer } from 'mobx-react';
import { Alert, Button, Grid, Segmented, Space, Tooltip, Typography } from 'antd';
import type {
  ComparisonControl,
  ComparisonType,
  RunComparison,
  ComparisonSide,
  RunUsage,
  ScenarioComparisonRow,
  UsageLine,
} from '@/api/validationRuns';
import { StatusPill } from '@/components/StatusPill';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import {
  CHANGE,
  CONTROL_STATE,
  ORDER,
  TYPE_LABEL,
  TYPE_ORDER,
  VERDICT_ALERT,
  identityLabel,
  shortRevision,
} from './compareWords';
import { formatMs, recordedUsage } from '@/api/runEvidence';
import { TrialDots } from './TrialDots';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// The Compare view (SI-07, mockup 09 frame A/B): the comparison type with what it must hold the same, the identities
// (Agent · Candidate revision · Actual deployment), the comparability banner, results regressions first with every
// trial, latency and cost with the sample behind each figure, and the release line — only where criteria exist, never a
// winner. Every verdict and sentence is the server's (ValidationRunComparisonRules); this lays them out.
// ─────────────────────────────────────────────────────────────────────────────

export const ComparisonView = observer(function ComparisonView({
  comparison,
  onTypeChange,
  onOpenResult,
}: {
  comparison: RunComparison;
  onTypeChange?: (type: ComparisonType) => void;
  onOpenResult: (resultId: number) => void;
}) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const muted = isDark ? colors.slate[400] : colors.slate[500];
  const border = isDark ? colors.slate[700] : colors.slate[200];
  const panel = isDark ? colors.slate[800] : '#FFFFFF';
  const c = comparison.comparability;
  const [showPassing, setShowPassing] = useState(false);

  const groups = ORDER.map((change) => ({ change, rows: comparison.scenarios.filter((s) => s.change === change) }))
    .filter((g) => g.rows.length > 0);
  const baselineLabel = identityLabel(comparison.baselineIdentity, comparison.baselineRunId);
  const candidateLabel = identityLabel(comparison.candidateIdentity, comparison.candidateRunId);

  const card = (title: string, children: ReactNode, aside?: ReactNode) => (
    <section
      aria-label={title}
      style={{ border: `1px solid ${border}`, borderRadius: 10, background: panel, padding: '14px 16px', minWidth: 0 }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        <Text strong>{title}</Text>
        {aside}
      </div>
      {children}
    </section>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
      {c && (
        card('Comparison type', (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
              <Segmented<ComparisonType>
                value={c.type}
                onChange={(v) => onTypeChange?.(v)}
                options={TYPE_ORDER.map((t) => {
                  const option = c.types.find((o) => o.type === t);
                  return {
                    value: t,
                    disabled: !option?.selectable && t !== c.type,
                    label: option?.selectable || t === c.type
                      ? TYPE_LABEL[t]
                      : <Tooltip title={option?.reason}><span>{TYPE_LABEL[t]}</span></Tooltip>,
                  };
                })}
              />
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {`What ${articleOf(c.type)} must hold the same, and what it lets differ:`}
            </Text>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {c.controls.map((ctl) => (
                <Tooltip key={ctl.key} title={ctl.note ?? undefined}>
                  <span tabIndex={0} aria-label={`${ctl.label}: ${controlWords(ctl.state, ctl.allowedToChange)}${ctl.note ? `. ${ctl.note}` : ''}`}>
                    <StatusPill tone={ctl.key === c.brokenControl ? 'red' : ctl.allowedToChange ? 'ink' : CONTROL_STATE[ctl.state].tone}>
                      {`${ctl.label}: ${controlWords(ctl.state, ctl.allowedToChange)}`}
                    </StatusPill>
                  </span>
                </Tooltip>
              ))}
            </div>
            {c.types.some((o) => !o.selectable && o.type !== c.type) && (
              <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {c.types.filter((o) => !o.selectable && o.type !== c.type).map((o) => (
                  <li key={o.type}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      <Text type="secondary" strong style={{ fontSize: 12 }}>{`${TYPE_LABEL[o.type]} is greyed out: `}</Text>
                      {o.reason}
                    </Text>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ), <StatusPill tone="ink">{TYPE_LABEL[c.type]}</StatusPill>)
      )}

      {(comparison.baselineIdentity || comparison.candidateIdentity) && (
        card('Agent · Candidate revision · Actual deployment', (
          <IdentityTable baseline={comparison.baselineIdentity} candidate={comparison.candidateIdentity}
            baselineRunId={comparison.baselineRunId} candidateRunId={comparison.candidateRunId} />
        ))
      )}

      {c && (
        <Alert
          type={VERDICT_ALERT[c.verdict]}
          showIcon
          title={c.headline}
          description={
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {c.detail && <span>{c.detail}</span>}
              {c.action && <Text strong>{`${c.verdict === 'Unavailable' ? 'Next' : 'To make it fair'}: ${c.action}.`}</Text>}
            </div>
          }
        />
      )}
      {c?.warnings.map((w) => (
        <Alert key={w.key} type={w.key.startsWith('price') ? 'warning' : 'info'} showIcon title={w.headline}
          description={w.action ? `${w.detail} ${w.action}.` : w.detail} />
      ))}
      {comparison.notes.length > 0 && (
        <Alert type="info" showIcon title="Read with care" description={comparison.notes.join(' ')} />
      )}

      {/* Nothing paired (a run still running): no counts, results or usage are shown as if the pair were complete. */}
      {comparison.scenarios.length > 0 && <>
      <Space size={[8, 8]} wrap aria-label="Counts">
        {groups.map((g) => (
          <StatusPill key={g.change} tone={CHANGE[g.change].tone}>{`${CHANGE[g.change].label}: ${g.rows.length}`}</StatusPill>
        ))}
      </Space>

      {card('Results per scenario, regressions first', (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {comparison.pairing?.oneResultPerScenario
              ? 'One dot per trial; each opens its trial. Repeats are recorded once ValidationTrial lands (SI-03), so each scenario has one trial per run today.'
              : 'One dot per trial — pass, fail, or a hollow dot for a trial that did not complete; each opens its trial. No repeat is chosen over another.'}
          </Text>
          {groups.map((g) => {
            const collapsed = g.change === 'StillPassing' && !showPassing;
            return (
              <section key={g.change} aria-label={CHANGE[g.change].label}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                  <Text strong>{`${CHANGE[g.change].label} (${g.rows.length})`}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>{CHANGE[g.change].explain}</Text>
                  {g.change === 'StillPassing' && (
                    <Button size="small" type="link" onClick={() => setShowPassing((v) => !v)}>
                      {showPassing ? 'Hide' : `Show ${g.rows.length}`}
                    </Button>
                  )}
                </div>
                {!collapsed && g.rows.map((row, i) => (
                  <ResultRow key={row.scenarioId} row={row} first={i === 0} divider={border} muted={muted}
                    baselineLabel={baselineLabel} candidateLabel={candidateLabel} onOpen={onOpenResult} />
                ))}
              </section>
            );
          })}
        </div>
      ))}

      {(comparison.baselineUsage || comparison.candidateUsage) && card('Latency and cost', (
        <UsageTable baseline={comparison.baselineUsage} candidate={comparison.candidateUsage}
          baselineLabel={baselineLabel} candidateLabel={candidateLabel} muted={muted} />
      ))}

      {card('Release criteria for this intended use', (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {comparison.releaseCriteria
            ? <Text>{comparison.releaseCriteria}</Text>
            : <Text>No predefined release criteria exist for this intended use yet, so this comparison makes no release statement.</Text>}
          {comparison.sampleStatement && <Text type="secondary">{comparison.sampleStatement}</Text>}
        </div>
      ), comparison.releaseCriteria ? undefined : <StatusPill tone="slate">Not defined</StatusPill>)}
      </>}
    </div>
  );
});

/** "held the same" · "the difference under test" · "not recorded yet" — what one control says about the pair. */
function controlWords(state: ComparisonControl['state'], allowed?: boolean): string {
  if (allowed) return state === 'Changed' ? 'the difference under test' : state === 'Held' ? 'allowed to differ, unchanged' : CONTROL_STATE[state].label.toLowerCase();
  return CONTROL_STATE[state].label.toLowerCase();
}

function articleOf(type: ComparisonType): string {
  return type === 'AgentChange' || type === 'EvaluatorChange' ? `an ${TYPE_LABEL[type].toLowerCase()}` : `a ${TYPE_LABEL[type].toLowerCase()}`;
}

/** One row of a two-sided table: a label (and why it matters), then the baseline's and the candidate's value. */
interface SideRow {
  label: string;
  hint?: string;
  baseline: ReactNode;
  candidate: ReactNode;
}

/**
 * Baseline beside candidate. Wide: label | baseline | candidate. Narrow (phones): the label is a full-width row above the
 * two values, so neither value is squeezed into a third of 375 px.
 */
const SideBySide = observer(function SideBySide({
  label,
  rows,
  baselineHeader,
  candidateHeader,
}: {
  label: string;
  rows: SideRow[];
  baselineHeader: ReactNode;
  candidateHeader: ReactNode;
}) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const screens = Grid.useBreakpoint();
  const narrow = !screens.md;
  const border = isDark ? colors.slate[700] : colors.slate[200];
  const muted = isDark ? colors.slate[400] : colors.slate[500];
  const cell = { minWidth: 0, overflowWrap: 'anywhere' as const, paddingTop: 8, borderTop: `1px solid ${border}` };
  return (
    <div
      role="table"
      aria-label={label}
      style={{
        display: 'grid',
        gridTemplateColumns: narrow ? 'minmax(0, 1fr) minmax(0, 1fr)' : 'minmax(140px, 0.7fr) minmax(0, 1fr) minmax(0, 1fr)',
        gap: narrow ? '4px 12px' : '0 16px',
        fontSize: 13,
      }}
    >
      {!narrow && <span role="columnheader" />}
      <div role="columnheader" style={{ minWidth: 0, overflowWrap: 'anywhere', paddingBottom: 6 }}>{baselineHeader}</div>
      <div role="columnheader" style={{ minWidth: 0, overflowWrap: 'anywhere', paddingBottom: 6 }}>{candidateHeader}</div>
      {rows.map((r) => (
        <div key={r.label} role="row" style={{ display: 'contents' }}>
          <span role="rowheader" style={narrow ? { gridColumn: '1 / -1', ...cell, paddingTop: 10 } : { ...cell, paddingBottom: 8 }}>
            <Text type="secondary">{r.label}</Text>
            {r.hint && <Text style={{ display: narrow ? 'inline' : 'block', fontSize: 11, color: muted }}>{narrow ? ` · ${r.hint}` : r.hint}</Text>}
          </span>
          <div role="cell" style={narrow ? { minWidth: 0, overflowWrap: 'anywhere' } : { ...cell, paddingBottom: 8 }}>{r.baseline}</div>
          <div role="cell" style={narrow ? { minWidth: 0, overflowWrap: 'anywhere' } : { ...cell, paddingBottom: 8 }}>{r.candidate}</div>
        </div>
      ))}
    </div>
  );
});

const header = (side: string, detail: string) => (
  <span>
    <Text strong>{side}</Text>
    <Text type="secondary" style={{ display: 'block', fontSize: 11 }}>{detail}</Text>
  </span>
);

const IdentityTable = observer(function IdentityTable({
  baseline,
  candidate,
  baselineRunId,
  candidateRunId,
}: {
  baseline?: ComparisonSide | null;
  candidate?: ComparisonSide | null;
  baselineRunId: number;
  candidateRunId: number;
}) {
  const of = (pick: (id: ComparisonSide, runId: number) => ReactNode) => ({
    baseline: baseline ? pick(baseline, baselineRunId) : '—',
    candidate: candidate ? pick(candidate, candidateRunId) : '—',
  });
  const rows: SideRow[] = [
    { label: 'Agent', ...of((id) => id.agentName ?? `Agent #${id.agentId}`) },
    {
      label: 'Candidate revision',
      ...of((id) => (
        <span>
          {shortRevision(id)}
          {id.candidateChanges.length > 0 && <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>{id.candidateChanges.join(' ')}</Text>}
        </span>
      )),
    },
    { label: 'Configured model', ...of((id) => id.configuredModel ?? 'Not recorded') },
    {
      label: 'Actual deployment',
      hint: 'what answered each round',
      ...of((id) => (
        <span>
          {id.answeredBy}
          {id.selectedMatchesActual === false && <StatusPill tone="amber">Differs from the configured model</StatusPill>}
        </span>
      )),
    },
    { label: 'Peers and skill router', ...of(() => 'Their own configured models') },
    { label: 'Evaluator', ...of((id) => id.evaluator) },
    {
      label: 'Run',
      ...of((id, runId) => `#${runId} · ${new Date(id.runDate).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })} · ${id.trials} trial${id.trials === 1 ? '' : 's'}`),
    },
  ];
  return (
    <SideBySide label="Who each side was" rows={rows}
      baselineHeader={header('Baseline', `run #${baselineRunId}`)} candidateHeader={header('Candidate', `run #${candidateRunId}`)} />
  );
});

function ResultRow({
  row,
  first,
  divider,
  muted,
  baselineLabel,
  candidateLabel,
  onOpen,
}: {
  row: ScenarioComparisonRow;
  first: boolean;
  divider: string;
  muted: string;
  baselineLabel: string;
  candidateLabel: string;
  onOpen: (resultId: number) => void;
}) {
  const side = (label: string, r: ScenarioComparisonRow['baseline']) => (
    <div style={{ flex: '1 1 200px', minWidth: 0 }}>
      <Text style={{ display: 'block', fontSize: 11, color: muted, overflowWrap: 'anywhere' }}>{label}</Text>
      {r ? (
        <Space size={8} wrap>
          <TrialDots side={r} onOpen={onOpen} />
          <Text style={{ fontSize: 12 }}>{r.summary ?? (r.completed ? (r.passed ? 'Passed' : 'Failed') : 'Did not complete')}</Text>
        </Space>
      ) : (
        <Text type="secondary" style={{ fontSize: 12 }}>Not in this run</Text>
      )}
    </div>
  );
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', padding: '10px 0', borderTop: first ? undefined : `1px solid ${divider}` }}>
      <div style={{ flex: '1 1 100%', minWidth: 0 }}>
        <Text style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, wordBreak: 'break-word' }}>{row.scenarioId}</Text>
        {(row.change === 'NotComparable' || row.change === 'Inconclusive') && (
          <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>{row.reason}</Text>
        )}
      </div>
      {side(baselineLabel, row.baseline)}
      {side(candidateLabel, row.candidate)}
    </div>
  );
}

const UsageTable = observer(function UsageTable({
  baseline,
  candidate,
  baselineLabel,
  candidateLabel,
  muted,
}: {
  baseline?: RunUsage | null;
  candidate?: RunUsage | null;
  baselineLabel: string;
  candidateLabel: string;
  muted: string;
}) {
  const line = (u: RunUsage | null | undefined, key: string): UsageLine | undefined => u?.lines.find((l) => l.key === key);
  const notRecorded = (u: RunUsage | null | undefined) => u && !u.recorded ? (u.notRecordedReason ?? 'Not recorded') : null;
  const sample = (text: ReactNode, n: string) => (
    <span>
      {text}
      {n && <Text style={{ display: 'block', fontSize: 11, color: muted }}>{n}</Text>}
    </span>
  );
  const latency = (u: RunUsage | null | undefined) => {
    const l = line(u, 'agent');
    if (notRecorded(u)) return <Text type="secondary">{notRecorded(u)}</Text>;
    if (!l || l.calls === 0) return <Text type="secondary">No agent rounds recorded</Text>;
    return sample(`${formatMs(l.medianMs)} · ${formatMs(l.p90Ms)}`, `n = ${l.calls} round${l.calls === 1 ? '' : 's'}`);
  };
  const endToEnd = (u: RunUsage | null | undefined) =>
    !u || u.trials === 0 ? <Text type="secondary">No trials</Text> : sample(`${formatMs(u.trialMedianMs)} · ${formatMs(u.trialP90Ms)}`, `n = ${u.trials} trial${u.trials === 1 ? '' : 's'}`);
  const cost = (u: RunUsage | null | undefined, key: string) => {
    if (notRecorded(u)) return <Text type="secondary">Not recorded</Text>;
    const l = line(u, key);
    if (!l) return <Text type="secondary">—</Text>;
    return sample(l.words, l.unpricedCalls > 0 && l.pricedCalls > 0 ? `${l.unpricedCalls} unpriced, not counted as $0` : '');
  };
  // The one way a recorded cost is written in Studio (recordedUsage, shared with the run page and the Debugger).
  const whole = (u: RunUsage | null | undefined) => !u ? <Text type="secondary">Not recorded</Text>
    : !u.recorded ? <Text type="secondary">{u.notRecordedReason ?? 'Not recorded'}</Text>
    : <Text strong>{recordedUsage(u.whole.pricedCalls > 0 ? u.whole.pricedUsd : null, u.whole.unpricedCalls)}</Text>;
  const note = (key: string) => line(baseline, key)?.note ?? line(candidate, key)?.note ?? undefined;
  const rows: SideRow[] = [
    { label: 'Model-call latency, agent rounds', hint: 'median · 90th percentile', baseline: latency(baseline), candidate: latency(candidate) },
    { label: 'End-to-end time per trial', hint: 'median · 90th percentile, includes peers and tools', baseline: endToEnd(baseline), candidate: endToEnd(candidate) },
    { label: 'Agent model cost', hint: 'whole run', baseline: cost(baseline, 'agent'), candidate: cost(candidate, 'agent') },
    { label: 'Peers', hint: note('peers') ?? 'their own models', baseline: cost(baseline, 'peers'), candidate: cost(candidate, 'peers') },
    { label: 'Skill router', hint: note('router') ?? 'its configured model', baseline: cost(baseline, 'router'), candidate: cost(candidate, 'router') },
    { label: 'Evaluator', hint: note('evaluator') ?? 'semantic judgments', baseline: cost(baseline, 'evaluator'), candidate: cost(candidate, 'evaluator') },
    { label: 'Whole run', hint: 'recorded after execution', baseline: whole(baseline), candidate: whole(candidate) },
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <SideBySide label="Latency and cost" rows={rows}
        baselineHeader={header('Baseline', baselineLabel)} candidateHeader={header('Candidate', candidateLabel)} />
      <Text type="secondary" style={{ fontSize: 12 }}>
        Prices come from Model pricing. Unpriced calls are counted beside the priced total, never added as $0. Model-call latency is one model round; end-to-end time is the patient's wait.
      </Text>
    </div>
  );
});
